// PanelSettings.tsx - Panel management section
import React, { useState } from 'react';
import { Button, Table, Space, Badge, message, Popconfirm, Switch } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useSuperAdmin } from '../../contexts/SuperAdminContext';
import { useEffectiveAdminId } from '../../contexts/AdminSelectorContext';
import { PanelModal } from './modals/PanelModal';
// import { getAndSanitize } from '../../shared/utils/sanitization';

export const PanelSettings: React.FC = () => {
  const [modalState, setModalState] = useState<{
    open: boolean;
    mode: 'create' | 'edit';
    data?: any;
  }>({ open: false, mode: 'create' });

  const { panels, deletePanel, togglePanel } = useSuperAdmin();
  const effectiveAdminId = useEffectiveAdminId();
  // const currentUserId = getAndSanitize('userId');
  // const role = getAndSanitize('role');

  // Filter panels based on effective admin ID
  const filteredPanels = effectiveAdminId === null
    ? panels // SuperAdmin viewing all
    : panels.filter(p => {
        if (p.users && p.users.length > 0) {
          return p.users.some((u: any) => u.userId === effectiveAdminId);
        }
        return false;
      });

  const handleCreate = () => {
    setModalState({ open: true, mode: 'create' });
  };

  const handleEdit = (panel: any) => {
    setModalState({ open: true, mode: 'edit', data: panel });
  };

  const handleDelete = (id: string) => {
    deletePanel(id);
    message.success('Panel deleted successfully');
  };

  const handleToggle = (panel: any) => {
    togglePanel({ id: panel.id, isActive: !panel.isActive });
    message.success(`Panel ${panel.name} ${!panel.isActive ? 'activated' : 'deactivated'}`);
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: 'Base URL',
      dataIndex: 'baseUrl',
      key: 'baseUrl',
      render: (url: string) => (
        <a href={url} target="_blank" rel="noopener noreferrer">
          {url}
        </a>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (record: any) => (
        <Badge
          status={record.isActive ? 'success' : 'default'}
          text={record.isActive ? 'Active' : 'Inactive'}
        />
      ),
    },
    {
      title: 'Active',
      key: 'active',
      render: (record: any) => (
        <Switch
          checked={record.isActive}
          onChange={() => handleToggle(record)}
          size="small"
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: any) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure?"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button icon={<DeleteOutlined />} danger size="small">
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          Create Panel
        </Button>
      </div>

      <Table
        dataSource={filteredPanels}
        columns={columns}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />

      <PanelModal
        open={modalState.open}
        mode={modalState.mode}
        initialData={modalState.data}
        onClose={() => setModalState({ open: false, mode: 'create' })}
      />
    </div>
  );
};
